import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { listDominoAccounts, type DirectoryAccount } from '@/lib/account-directory';

/**
 * « Rattacher à… » (26/10/2026): a company known only by its name is attached,
 * by a person, to the exact company (HubSpot) it is. Its work, facts and name
 * move there; nothing is matched on the name automatically.
 */
export const attachCompanyToAccount = async (fromSubjectId: string, intoSubjectId: string): Promise<void> => {
  const { error } = await supabase.rpc('attach_company_to_account_i1', {
    p_from_subject_id: fromSubjectId,
    p_into_subject_id: intoSubjectId,
  });
  if (error) throw new Error('Le rattachement n’a pas pu être fait.');
};

export const AttachAccountControl = ({ subjectId, label, onAttached }: {
  subjectId: string;
  label: string;
  /** Reload what lists the accounts (the name-only one is gone). */
  onAttached?: () => void;
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(label);
  const [results, setResults] = useState<DirectoryAccount[] | null>(null);
  const [chosen, setChosen] = useState<DirectoryAccount | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const timer = window.setTimeout(() => {
      void listDominoAccounts(query).then((page) => {
        if (active) setResults(page.items.filter((account) => account.subjectId !== subjectId));
      }).catch(() => { if (active) setError('Les comptes n’ont pas pu être chargés.'); });
    }, 200);
    return () => { active = false; window.clearTimeout(timer); };
  }, [open, query, subjectId]);

  const confirm = async () => {
    if (!chosen) return;
    setPending(true);
    setError(null);
    try {
      await attachCompanyToAccount(subjectId, chosen.subjectId);
      onAttached?.();
      navigate(`/threads/${encodeURIComponent(chosen.subjectId)}`, { replace: true });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Le rattachement n’a pas pu être fait.');
      setPending(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="inline-flex h-8 items-center rounded-lg border border-domino-cardBorder bg-domino-white px-3 text-xs font-medium text-domino-ink transition-[background-color,transform] duration-150 ease-snap hover:bg-domino-muted active:scale-[0.97]">
        Rattacher à…
      </button>
    );
  }

  return (
    <div className="mt-2 basis-full max-w-md rounded-xl border border-domino-cardBorder bg-domino-white p-3 motion-safe:animate-fadeIn">
      {chosen ? (
        <div className="space-y-3 text-sm">
          <p className="text-domino-ink">Rattacher « {label} » au compte HubSpot <b>{chosen.displayName}</b> ? Son travail et ce qu’on en sait y passent.</p>
          <div className="flex gap-2">
            <button type="button" disabled={pending} onClick={() => void confirm()}
              className="h-8 rounded-lg bg-domino-aubergine px-3 text-xs font-semibold text-white active:scale-[0.97] disabled:opacity-60">
              {pending ? 'Rattachement…' : 'Rattacher'}
            </button>
            <button type="button" disabled={pending} onClick={() => setChosen(null)} className="h-8 px-2 text-xs text-domino-inkSoft hover:text-domino-ink">Retour</button>
          </div>
        </div>
      ) : (
        <>
          <label className="block text-xs font-medium text-domino-inkSoft" htmlFor="attach-account-query">Compte HubSpot</label>
          <input id="attach-account-query" autoFocus value={query} onChange={(event) => setQuery(event.target.value)}
            className="mt-1 h-9 w-full rounded-lg border border-domino-cardBorder px-3 text-sm focus:outline-none focus:ring-2 focus:ring-domino-prune" />
          <ul className="mt-2 max-h-56 overflow-y-auto">
            {results?.map((account) => (
              <li key={account.subjectId}>
                <button type="button" onClick={() => setChosen(account)}
                  className="w-full rounded-lg px-2 py-1.5 text-left text-sm text-domino-ink hover:bg-domino-muted">
                  {account.displayName}
                </button>
              </li>
            ))}
            {results?.length === 0 && <li className="px-2 py-1.5 text-sm text-domino-inkSoft">Aucun compte HubSpot de ce nom.</li>}
          </ul>
          <button type="button" onClick={() => setOpen(false)} className="mt-1 text-xs text-domino-inkSoft hover:text-domino-ink">Fermer</button>
        </>
      )}
      {error && <p role="alert" className="mt-2 text-xs text-domino-inkSoft">{error}</p>}
    </div>
  );
};
